'use client';
import { useEffect, useCallback } from 'react';

interface PhotoLightboxProps {
  photos: string[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function PhotoLightbox({ photos, currentIndex, onClose, onNavigate }: PhotoLightboxProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
    if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) onNavigate(currentIndex + 1);
  }, [currentIndex, photos.length, onClose, onNavigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  if (!photos.length) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
      <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white text-3xl hover:text-gray-300">&times;</button>
        <img src={photos[currentIndex]} alt="" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        {photos.length > 1 && (
          <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-2 pointer-events-none">
            {currentIndex > 0 ? (
              <button onClick={() => onNavigate(currentIndex - 1)} className="pointer-events-auto bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 text-xl">&lsaquo;</button>
            ) : <span />}
            {currentIndex < photos.length - 1 ? (
              <button onClick={() => onNavigate(currentIndex + 1)} className="pointer-events-auto bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 text-xl">&rsaquo;</button>
            ) : <span />}
          </div>
        )}
        <div className="text-center text-white mt-2 text-sm">{currentIndex + 1} / {photos.length}</div>
      </div>
    </div>
  );
}
